import { useEffect, useState, type ReactNode } from 'react';
import { Icon } from '../components/Icon';
import { api, ApiError } from '../lib/api';
import { ActiveJobBanner } from '../components/AuditJob';
import { GroupBar, GroupScheduleForm, type AuditGroup, type GroupsResponse } from '../components/AuditGroups';

interface TriggerAuditProps {
  onSubmit: () => void;
  onCancel: () => void;
}

interface MeResponse { user: { email: string; name: string | null; role: string } }
interface FranchiseesResponse { states: { state: string; site_count: number; af_count: number; enabled?: boolean }[] }
interface PendingResponse { pending: number; running: boolean }

export function TriggerAudit({ onSubmit, onCancel }: TriggerAuditProps) {
  const [scope, setScope] = useState<'all_enabled' | 'selected_afs'>('all_enabled');
  const [selectedStates, setSelectedStates] = useState<string[]>(['IL']);
  const [afNumbers, setAfNumbers] = useState('');
  const [windowDays, setWindowDays] = useState(90);
  const [forceRerun, setForceRerun] = useState(false);
  const [priority, setPriority] = useState<'low' | 'normal' | 'high'>('normal');
  const [syncZoho, setSyncZoho] = useState(true);
  const [syncAxiscare, setSyncAxiscare] = useState(true);
  const [notify, setNotify] = useState(true);
  const [notes, setNotes] = useState('');
  const [me, setMe] = useState<{ email: string; name: string | null; role: string } | null>(null);
  const [groups, setGroups] = useState<AuditGroup[]>([]);
  const [schedulerOn, setSchedulerOn] = useState(false);
  const [activeGroupId, setActiveGroupId] = useState<number | null>(null);
  const [stateRollup, setStateRollup] = useState<{ state: string; site_count: number; af_count: number; enabled?: boolean }[]>([]);
  const [pending, setPending] = useState<PendingResponse | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [refreshTick, setRefreshTick] = useState(0);

  useEffect(() => {
    api<MeResponse>('/api/auth/me').then((r) => setMe(r.user)).catch(() => {});
    api<FranchiseesResponse>('/api/audit-jobs/franchisee-states').then((r) => setStateRollup(r.states)).catch(() => {});
    api<PendingResponse>('/api/audit-jobs/pending-count').then((r) => setPending(r)).catch(() => {});
    loadGroups();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function loadGroups(selectId?: number | null) {
    api<GroupsResponse>('/api/audit-groups')
      .then((r) => { setGroups(r.groups); setSchedulerOn(r.scheduler_enabled); if (selectId !== undefined) setActiveGroupId(selectId); })
      .catch(() => {});
  }

  const loadGroup = (g: AuditGroup | null) => {
    setActiveGroupId(g?.id ?? null);
    if (!g) return;
    if (g.kind === 'states') { setScope('all_enabled'); setSelectedStates(g.members); }
    else { setScope('selected_afs'); setAfNumbers(g.members.join(', ')); }
  };
  const activeGroup = groups.find((g) => g.id === activeGroupId) ?? null;

  // Only states marked enabled in app_config are selectable. Older API responses
  // without the flag are treated as enabled so the page still works mid-deploy.
  const enabledStates = stateRollup.length > 0
    ? stateRollup.filter((s) => s.enabled !== false)
    : [{ state: 'IL', site_count: 0, af_count: 0 }];

  // Drop any selectedStates that are no longer enabled. If nothing remains,
  // fall back to selecting the full enabled set so "Trigger run" has scope.
  useEffect(() => {
    if (stateRollup.length === 0) return;
    const allowed = new Set(enabledStates.map((s) => s.state));
    const kept = selectedStates.filter((s) => allowed.has(s));
    if (kept.length === 0) {
      setSelectedStates(enabledStates.map((s) => s.state));
    } else if (kept.length !== selectedStates.length) {
      setSelectedStates(kept);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stateRollup]);

  const afTokenCount = afNumbers.trim().split(/[,\s]+/).filter(Boolean).length;
  const currentMembers = scope === 'all_enabled'
    ? selectedStates
    : afNumbers.split(/[,\s]+/).map((x) => x.trim().toUpperCase()).filter(Boolean);
  // A saved group can hold a state that is no longer enabled for audits; say so
  // instead of quietly auditing fewer states than the group lists.
  const disabledInGroup = scope === 'all_enabled' && stateRollup.length > 0
    ? selectedStates.filter((st) => !enabledStates.some((e) => e.state === st)) : [];
  const totalSitesInScope = scope === 'all_enabled'
    ? enabledStates.filter((s) => selectedStates.includes(s.state)).reduce((sum, s) => sum + s.site_count, 0)
    : afTokenCount;
  // A run needs a non-empty scope: at least one state in state-mode, or at
  // least one AF# in AF-mode. (Guards the "Trigger run" button.)
  const scopeReady = scope === 'selected_afs' ? afTokenCount > 0 : selectedStates.length > 0;
  const ingestEstimateSec = totalSitesInScope * 60;     // EVV ~30s + billing ~30s per site
  const auditEstimateSec  = Math.max(5, totalSitesInScope * 2);
  const totalEstimateMin  = ((syncAxiscare ? ingestEstimateSec : 0) + auditEstimateSec) / 60;

  const submit = async () => {
    setSubmitting(true); setError(null);
    const afs = scope === 'selected_afs' && afNumbers.trim()
      ? afNumbers.split(/[,\s]+/).map((x) => x.trim()).filter(Boolean)
      : undefined;
    const usingAfs = !!(afs && afs.length > 0);
    try {
      await api('/api/audit-jobs', {
        method: 'POST',
        body: {
          // In "Specific AF#s" mode the AF# list is the sole scope; the state
          // selector is hidden, so don't send a stale/default state that the
          // backend would AND against the AF# list and zero out the results.
          scope_states: usingAfs ? [] : selectedStates,
          ...(usingAfs ? { scope_af_numbers: afs } : {}),
          audit_window_days: windowDays,
          sync_zoho: syncZoho,
          sync_axiscare: syncAxiscare,
          priority,
          force_rerun: forceRerun,
          notes: notes || undefined,
          notify_email: notify && me?.email ? me.email : undefined,
        },
      });
      setRefreshTick((n) => n + 1);
      onSubmit();
    } catch (e) {
      const msg = e instanceof ApiError && (e.body as { error?: string })?.error
        ? (e.body as { error: string }).error
        : (e instanceof Error ? e.message : 'failed');
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const toggleState = (s: string) =>
    setSelectedStates((prev) => prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s]);

  return (
    <div className="scroll-y" style={{ flex: 1, padding: 24 }}>
      <div style={{ maxWidth: 880, margin: '0 auto' }}>
        <div style={{ marginBottom: 20 }}>
          <div style={{ fontSize: 11.5, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 600 }}>New audit run</div>
          <h1 style={{ margin: '4px 0 0', fontSize: 22, fontWeight: 600, letterSpacing: '-0.015em' }}>Trigger encroachment audit</h1>
          <div style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 4 }}>
            Optionally syncs Zoho + AxisCare first, then runs the v0.6 decision tree against the chosen window. Cases land in the manual review queue as the run completes.
          </div>
        </div>

        <ActiveJobBanner refreshKey={refreshTick} />

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 320px', gap: 20, alignItems: 'flex-start', marginTop: 16 }}>
          <div className="card" style={{ padding: 0 }}>
            <FormSection title="01 · Scope">
              <GroupBar
                groups={groups}
                activeId={activeGroupId}
                kind={scope === 'all_enabled' ? 'states' : 'afs'}
                members={currentMembers}
                onLoad={loadGroup}
                onChanged={(id) => loadGroups(id)}
              />
              <RadioGroup
                value={scope} onChange={(v) => setScope(v as typeof scope)}
                options={[
                  { id: 'all_enabled', label: 'States', desc: 'Audit every AF# in the selected states' },
                  { id: 'selected_afs', label: 'Specific AF#s', desc: 'Comma-separated. Use stems (AF-126) or specific entries (AF-126.2)' },
                ]}
              />
              {scope === 'all_enabled' && (
                <div style={{ marginTop: 12, padding: 12, background: 'var(--surface-2)', borderRadius: 'var(--radius)' }}>
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    {enabledStates.map((s) => (
                      <button
                        key={s.state}
                        className="btn btn-sm"
                        style={{
                          background: selectedStates.includes(s.state) ? 'var(--accent)' : 'var(--surface)',
                          color: selectedStates.includes(s.state) ? '#fff' : 'var(--text)',
                          borderColor: selectedStates.includes(s.state) ? 'var(--accent)' : 'var(--border)',
                        }}
                        onClick={() => toggleState(s.state)}
                      >
                        {s.state} <span style={{ opacity: 0.7, marginLeft: 6 }}>· {s.site_count} sites</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {scope === 'selected_afs' && (
                <>
                  <input
                    type="text"
                    className="input"
                    placeholder="AF-126, AF-240, AF-186"
                    value={afNumbers}
                    onChange={(e) => setAfNumbers(e.target.value)}
                    style={{ width: '100%', marginTop: 12, fontSize: 12.5 }}
                  />
                  <div className="muted" style={{ fontSize: 11.5, marginTop: 6 }}>
                    The AF# list drives the scope — state selection doesn&apos;t apply here, so AF#s from any state (IL, CA, PA…) are audited as entered.
                  </div>
                </>
              )}
              {disabledInGroup.length > 0 && (
                <div className="c-warn" style={{ fontSize: 12, marginTop: 8 }}>
                  Not enabled for audits on Configuration, so not shown above: {disabledInGroup.join(', ')}. A run will still include {disabledInGroup.length === 1 ? 'it' : 'them'}; remove {disabledInGroup.length === 1 ? 'it' : 'them'} from the group if that's not wanted.
                </div>
              )}
            </FormSection>

            {activeGroup && (
              <FormSection title={`Schedule · ${activeGroup.name}`}>
                <GroupScheduleForm
                  group={activeGroup}
                  schedulerEnabled={schedulerOn}
                  myEmail={me?.email ?? null}
                  isAdmin={me?.role === 'admin'}
                  onChanged={() => loadGroups()}
                />
              </FormSection>
            )}

            <FormSection title="02 · Window & priority">
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                <div>
                  <label className="form-lbl">Window (days back)</label>
                  <select className="select" value={String(windowDays)} onChange={(e) => setWindowDays(Number(e.target.value))}>
                    <option value="1">Last 24 hours (fast daily sweep)</option>
                    <option value="2">Last 48 hours (fast daily sweep)</option>
                    <option value="7">Last 7 days</option>
                    <option value="30">Last 30 days</option>
                    <option value="60">Last 60 days</option>
                    <option value="90">Last 90 days (spec default)</option>
                    <option value="180">Last 180 days</option>
                    <option value="365">Last 365 days</option>
                    <option value="2000">Last 2000 days (~5.5 yrs, full history)</option>
                  </select>
                </div>
                <div>
                  <label className="form-lbl">Run priority</label>
                  <select className="select" value={priority} onChange={(e) => setPriority(e.target.value as typeof priority)}>
                    <option value="low">Low — runs after pending normal jobs</option>
                    <option value="normal">Normal — FIFO within priority</option>
                    <option value="high">High — jumps the queue</option>
                  </select>
                </div>
              </div>
              <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginTop: 16, fontSize: 13, padding: '10px 12px', border: `1px solid ${forceRerun ? 'var(--accent)' : 'var(--border)'}`, borderRadius: 'var(--radius)' }}>
                <input type="checkbox" checked={forceRerun} onChange={(e) => setForceRerun(e.target.checked)} style={{ marginTop: 2, accentColor: 'var(--accent)' }} />
                <div>
                  <div style={{ fontWeight: 500, fontSize: 13 }}>Ignore suppressions for this run (diagnostic — normally leave off)</div>
                  <div className="muted" style={{ fontSize: 12 }}>Open cases (New / Reviewed / Notified / Acknowledged / Confirmed violation) are re-checked on every run whether or not this is ticked. Ticking it does two things for this run only: suppressed client × franchisee × zip pairs (including "Not a real client") are evaluated as if there were no suppression, so a new case can open; and dismissed cases are not automatically reopened when the issue persists. RESOLVED and DISMISSED cases are never changed.</div>
                </div>
              </label>
            </FormSection>

            <FormSection title="03 · Data freshness">
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, fontSize: 13 }}>
                <input type="checkbox" checked={syncZoho} onChange={(e) => setSyncZoho(e.target.checked)} style={{ accentColor: 'var(--accent)' }} />
                Sync Zoho first (franchisees + SPC ledger)
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
                <input type="checkbox" checked={syncAxiscare} onChange={(e) => setSyncAxiscare(e.target.checked)} style={{ accentColor: 'var(--accent)' }} />
                Pull fresh AxisCare data (visits, clients, caregivers, billing) — ~60s per site
              </label>
            </FormSection>

            <FormSection title="04 · Notes & notification">
              <label className="form-lbl">Reason / notes</label>
              <textarea
                className="textarea"
                placeholder="Optional. Saved with the job and included in the completion email."
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
              />
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 14, fontSize: 13 }}>
                <input type="checkbox" checked={notify} onChange={(e) => setNotify(e.target.checked)} style={{ accentColor: 'var(--accent)' }} />
                <span>Email me when run completes {me?.email && <span className="muted">({me.email})</span>}</span>
              </label>
            </FormSection>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 12, position: 'sticky', top: 16 }}>
            <div className="card card-pad">
              <div style={{ fontSize: 11.5, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 600 }}>Estimate</div>
              <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 10 }}>
                <Stat label="Sites in scope" value={totalSitesInScope.toLocaleString()} />
                <Stat label="Estimated runtime" value={`~${totalEstimateMin.toFixed(1)} min`} />
                <Stat label="Spec version" value={<span className="mono">v0.6.0</span>} />
                <Stat label="Queue" value={pending == null ? '…' : pending.running ? `${pending.pending} ahead · running` : pending.pending > 0 ? `${pending.pending} pending` : 'idle'} />
              </div>
              <div className="divider-h" style={{ margin: '14px 0 12px' }} />
              {error && <div className="c-warn" style={{ fontSize: 12, marginBottom: 8 }}>{error}</div>}
              <button
                className="btn btn-primary"
                style={{ width: '100%', height: 36 }}
                disabled={submitting || !scopeReady}
                onClick={submit}
              >
                <Icon name="play" size={12} /> {submitting ? 'Queueing…' : 'Trigger run'}
              </button>
              <button className="btn btn-ghost" style={{ width: '100%', marginTop: 6 }} onClick={onCancel}>Cancel</button>
            </div>

            <div className="card card-pad" style={{ background: 'var(--info-soft)', borderColor: 'oklch(from var(--info) l calc(c - 0.05) h)' }}>
              <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                <Icon name="alert" size={16} color="var(--info)" />
                <div style={{ fontSize: 12.5, lineHeight: 1.5 }}>
                  <div style={{ fontWeight: 500, color: 'var(--info)', marginBottom: 2 }}>How this works</div>
                  <span style={{ color: 'var(--text)' }}>The job runs in the background. Watch progress in the banner above; we&apos;ll email you when it&apos;s done if checked. Only one job runs at a time — extras wait their turn.</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function FormSection({ title, desc, children }: { title: string; desc?: string; children: ReactNode }) {
  return (
    <div style={{ padding: 20, borderBottom: '1px solid var(--border)' }}>
      <div style={{ fontSize: 13.5, fontWeight: 600, marginBottom: 2 }}>{title}</div>
      {desc && <div className="muted" style={{ fontSize: 12.5, marginBottom: 14 }}>{desc}</div>}
      <div style={{ marginTop: desc ? 0 : 12 }}>{children}</div>
    </div>
  );
}

function RadioGroup({
  value, onChange, options,
}: {
  value: string;
  onChange: (v: string) => void;
  options: { id: string; label: string; desc?: string }[];
}) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      {options.map((opt) => {
        const active = value === opt.id;
        return (
          <label key={opt.id} style={{
            display: 'flex', alignItems: 'flex-start', gap: 10,
            padding: '10px 12px',
            border: `1px solid ${active ? 'var(--accent)' : 'var(--border)'}`,
            background: active ? 'color-mix(in oklch, var(--accent) 5%, var(--surface))' : 'var(--surface)',
            borderRadius: 'var(--radius)',
          }}>
            <input type="radio" checked={active} onChange={() => onChange(opt.id)} style={{ marginTop: 2, accentColor: 'var(--accent)' }} />
            <div>
              <div style={{ fontWeight: 500, fontSize: 13 }}>{opt.label}</div>
              {opt.desc && <div className="muted" style={{ fontSize: 12 }}>{opt.desc}</div>}
            </div>
          </label>
        );
      })}
    </div>
  );
}

function Stat({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', fontSize: 13 }}>
      <span className="muted">{label}</span>
      <span className="num mono" style={{ fontWeight: 500 }}>{value}</span>
    </div>
  );
}
